import { useCallback, useMemo, useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Editor as ToastUiEditor } from "@toast-ui/react-editor"
import { CardBodyContent, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { Editor } from "@/components/ui/editor"
import { FormStatus } from "@/components/ui/form"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useEditorKeyDown } from "@/hooks/useEditorKeyDown"
import { CallCaseSummaryFeedbackButton } from "@/modules/patients/detail/progress-note/components/call-case-summary-feedback/call-case-summary-feedback-button"
import { AnamnesisAiButtons } from "@/modules/patients/detail/progress-note/components/progress-note-editable/anamnesis-ai-buttons"
import { useCallTranscriptSubscription } from "@/modules/patients/detail/progress-note/hooks/useCallTranscriptSubscription"
import { useProgressNoteAnamnesisForm } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteAnamnesisForm"
import { useProgressNoteAnamnesisFormEffect } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteAnamnesisFormEffect"
import { AnamnesisFormType, anamnesisSchema } from "@/modules/patients/detail/progress-note/schema/anamnesis-schema"
import useCallCaseSummaryFeedbackStore from "@/modules/patients/detail/progress-note/stores/useCallCaseSummaryFeedbackStore"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useUpdateProgressNoteAnamnesisMutation } from "@/modules/patients/services/useUpdateProgressNoteAnamnesisMutation"
import { QuestionnaireType } from "@/types/__generated__/union-types"

const AnamnesisSection = ({
  patient,
  appointmentContext,
  episodeOfCareProgressNoteData,
  questionnaireId,
  questionnaireType: _questionnaireType,
}: {
  patient: PatientFragment$data
  appointmentContext: PatientContextAppointmentFragment$data
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  questionnaireId: number | null | undefined
  questionnaireType: QuestionnaireType | null | undefined
}): React.JSX.Element => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const { id, anamnesis } = episodeOfCareProgressNoteData
  const { defaultValues, storeFormState } = useProgressNoteAnamnesisForm(id, anamnesis)
  const updateProgressNoteAnamnesis = useUpdateProgressNoteAnamnesisMutation(country)
  const anamnesisRef = useRef<ToastUiEditor>(null)

  useEditorKeyDown(anamnesisRef, true)
  useCallTranscriptSubscription(
    appointmentContext.id,
    id,
    country,
    appointmentContext.status,
    appointmentContext.channel,
  )

  // Check if call transcripts exist with content
  const hasCallTranscript = useMemo(() => {
    const callTranscripts =
      episodeOfCareProgressNoteData.__typename === "ch_ProgressNote"
        ? episodeOfCareProgressNoteData.ch_callTranscripts
        : episodeOfCareProgressNoteData.de_callTranscripts
    const transcriptEdges = callTranscripts?.edges ?? []
    return transcriptEdges.some((edge) => edge.node.transcript.some((t) => t.text?.trim()))
  }, [episodeOfCareProgressNoteData])

  const hasUsedCallCaseSummary = useCallCaseSummaryFeedbackStore((s) => !!s.hasUsedByAppointment[appointmentContext.id])

  const form = useForm<AnamnesisFormType>({
    resolver: zodResolver(anamnesisSchema),
    defaultValues: defaultValues,
    shouldFocusError: false,
  })

  useProgressNoteAnamnesisFormEffect(id, episodeOfCareProgressNoteData, storeFormState, form)
  const {
    formState: { isDirty },
    reset,
    handleSubmit,
  } = form

  const handleUpdate = useCallback(() => {
    if (isDirty) {
      FaroInsightsWrapper.pushEvent("progressNote_anamnesis_save")
      handleSubmit((formData) => {
        updateProgressNoteAnamnesis({ id, anamnesis: formData.anamnesis ?? "" }, () => {
          reset(formData, { keepIsSubmitted: true })
        })
      })()
    }
  }, [handleSubmit, id, isDirty, reset, updateProgressNoteAnamnesis])

  const handleForceUpdate = useCallback(() => {
    handleSubmit((formData) => {
      updateProgressNoteAnamnesis({ id, anamnesis: formData.anamnesis ?? "" }, () => {
        reset(formData, { keepIsSubmitted: true })
      })
    })()
  }, [handleSubmit, id, reset, updateProgressNoteAnamnesis])

  return (
    <section data-testid="anamnesis-section">
      <CardBodyHeader data-testid="anamnesis-title">
        <div className="flex items-center gap-0 self-center">
          <CardBodyHeaderTitle>{t("episodeOfCare.progressNote.anamnesis.title")}</CardBodyHeaderTitle>
          {appointmentContext && appointmentContext.status === "IN_PROGRESS" && (
            <>
              <AnamnesisAiButtons
                patient={patient}
                appointmentContext={appointmentContext}
                progressNoteId={id}
                questionnaireId={questionnaireId}
                hasCallTranscript={hasCallTranscript}
                form={form}
                onAfterApply={handleForceUpdate}
              />
              {hasUsedCallCaseSummary && <CallCaseSummaryFeedbackButton appointmentId={appointmentContext.id} />}
            </>
          )}
        </div>
        <div className="flex items-center gap-4 self-center pr-4">
          <FormStatus formControl={form} />
        </div>
      </CardBodyHeader>
      <CardBodyContent className="grid">
        <Controller
          control={form.control}
          name="anamnesis"
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Editor
              ref={anamnesisRef}
              minHeight="100px"
              className="[&_.toastui-editor-contents]:max-h-100 [&_.toastui-editor-contents]:overscroll-contain"
              autofocus={false}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={() => FaroInsightsWrapper.pushEvent("progressNote_anamnesis_focus")}
              onBlur={() => {
                FaroInsightsWrapper.pushEvent("progressNote_anamnesis_blur")
                handleUpdate()
              }}
              error={error?.message ? t(error.message) : null}
            />
          )}
        />
      </CardBodyContent>
    </section>
  )
}

export { anamnesisSchema, AnamnesisSection, type AnamnesisFormType }
